import { ReactComponent as IconDataExchange } from '@/assets/svgs/data-element-icon.svg';
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconDataExchangeError } from '@/assets/svgs/icon-status-error.svg';
import MultipleEmailAddressInput from './components/MultipleEmailAddressInput/index';
import { useEffect, useState, useMemo } from "react";
import useModal from '@/hooks/use-modal';
import Modal from '@/componentsDS/Modal/Modal';
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Button from "@/componentsDS/Button/Button";
import { ButtonType, ButtonVariant} from '@/componentsDS/Button/Button.type'
import PropTypes from "prop-types";
import DataExchangeTablePage from "./components/DataExchangeTable";
import useDataExchangeRelation from "@/hooks/use-data-exchange-relation/index";
import Toast from '@/components/Toast';
import useToast from '@/hooks/use-toast';
import api from '@/services/api/dataExchangeRelation.js';
import { useSelector } from 'react-redux';
import { isLastCombination } from './helpers/index';
import InputDropdown from '@/dsComponents/InputDropdownSelect/index';
import Portal from '@/components/Portal/index';
import { featureConstant } from "@/constants/configuration/feature";
import { useFeature } from "@/hooks/use-feature/index.js";

export const renderMessage = (action) => {
	switch (action) {
		case "REJECTED":
			return (
				<div className="on-boarding__card-configure__body-content">
					{"You are about to reject this data exchange relationship."}
					<div>
						{"Do you want to proceed to reject?"}
					</div>
				</div>
			)
		case "REVOKED":
			return (
				<div className="on-boarding__card-configure__body-content">
					{"You are about to revoke this data exchange relationship."}
					<div>
						{"Do you want to proceed to revoke?"}
					</div>
				</div>
			)
		case "REMOVE":
			return (
				<div className="on-boarding__card-configure__body-content">
					{"You are about to remove this data exchange relationship."}
					<div>
						{"Do you want to proceed to remove?"}
					</div>
				</div>
			)
		case "LAST_RECORD":
			return (
				<div className="on-boarding__card-configure__body-content">
					{"Canceling this request will remove only the Solution Provider (via),"}
					<div>
						{"as your counterparty has already accepted the relationship."}
					</div>
					{" Are you sure you want to proceed?"}

				</div>
			)
		case "CANCELLED":
			return (
				<div className="on-boarding__card-configure__body-content">
					{"You are about to cancel this data exchange relationship."}
					<div>
						{"Do you want to proceed to cancel?"}
					</div>
				</div>
			)
		case "DISABLED_COPY_TRANSACTION":
			return (
				<div className="on-boarding__card-configure__body-content">
					{"Once disabled, transaction copies will no longer be sent to the"}
					<div>
						{"selected pitstop. Are you sure you want to disable it?"}
					</div>
				</div>
			)
			
		case "ENABLED_COPY_TRANSACTION":
			return (
				<div className="on-boarding__card-configure__body-content__left">
					{"Transaction copies will be sent to the selected pitstop when this Data"}
					<div>
						{"Element is shared on behalf of your organization."}
					</div>
				</div>
			)
	}
}

export const renderAPIAction = (action) => {
	switch (action) {
		case "APPROVED":
			return { apiCall: api.dataExchangeRelationsApprove, successMessage: "Data exchange relationship approved" };
		case "REJECTED":
			return { apiCall: api.dataExchangeRelationsReject, successMessage: "Data exchange relationship rejected" };
		case "REVOKED":
			return { apiCall: api.dataExchangeRelationsRevoke, successMessage: "Data exchange relationship revoked" };
		case "REMOVE":
			return { apiCall: api.dataExchangeRelationsRemove, successMessage: "Data exchange relationship removed" };
		case "CANCELLED":
			return { apiCall: api.dataExchangeRelationsCancel, successMessage: "Data exchange relationship cancelled" };
		case "LAST_RECORD":
			return { apiCall: api.editDataExchangeRelationship, successMessage: "Data exchange relationship cancelled" };
	}
}

export function RenderModalTitle(selectedAction) {
	const titleMap = {
		DISABLED_COPY_TRANSACTION: "Disable Transaction Copy",
		ENABLED_COPY_TRANSACTION: "Enable Transaction Copy",
	};
	return <div className="card-configure__header__left">
		<div className="card-configure__title">
			<div className="card-configure__title__left">
				{(selectedAction === "APPROVED" || selectedAction === "ENABLED_COPY_TRANSACTION") ? <div className='card-configure__title__left__icon'>
					<div className='DS__icon'>
					<IconDataExchange />
					</div>
				</div> : <div className='card-configure__title__left__icon__reject'>
					<IconDataExchangeError />
				</div>}
			</div>
			<div className="card-configure__title__right">
				<div className="card-configure__title__right__top">
					<div className='card-configure__title__right__top__title'>
						{titleMap[selectedAction] || "Data Exchange Consent"}
					</div>
				</div>
			</div>
		</div>
	</div>;
}

export function getPayloadForDataExchangeRelationship(data, enableCopy, selectPitstops) {
	return {
		dataExchangeRelationshipId: data?.dataExchangeRelationshipId,
		dataExchangeRelationshipInvitesId: data?.dataExchangeRelationshipInvitesId,
		serviceProviderRelationshipId: data?.serviceProviderRelationshipId,
		serviceProviderRelationshipInvitesId: data?.serviceProviderRelationshipInvitesId,
		serviceProviderOrgId: data?.serviceProviderOrgId,
		ccToContributor: enableCopy,
		...(enableCopy && {
			contributorOrgId: selectPitstops[0]?.orgId,
			contributorSystemId: selectPitstops[0]?.value,
		}),
	};
}

const DataExchangePage = ({
	defaultSelectedAction = null,
	defaultSelectedData = null
}) => {
	const title = "My Data Exchange";
	const description = "Manage the data exchange relationships with organizations you would like to contribute data to and consume data from.";
	const groupBy = "dataElementName";
	const emptyMessage = "No Data Exchange Relationship created yet.";

	const {
		fetchDataExchange,
		dataExchangeListing,
		fetchOrgData,
		allOrgData,
		fetchServiceProviderRelation,
		serviceProviderRelation,
		fetchPitstops,
		pitstops,
		fetchDataElements,
		dataElement,
		dataExchangeLoading,
		orgDataLoading,
		serviceProviderRelationLoading,
		dataElementLoading,
		pitstopsLoading
	 } = useDataExchangeRelation();
	const [refreshData, setRefreshData] = useState(false);

	// Get Profile
	const profile = useSelector(state => state.auth.profile);
	const orgId = profile?.organizationId;
	const toastType = {
		success: "success",
		error: "error"
	}
	const { toastList, addToastMessage } = useToast()
	const [loading, setLoading] = useState(false);

	// Confirm Modal for Approve and Reject
	const { visible: initiateActionButtonModal, show: showActionButtonModal, hide: hideActionButtonModal } = useModal();
	const { visible: initiateLastRecordModal, show: showLastRecordModal, hide: hideLastRecordModal } = useModal();

	/*=================================================
		Handle Submit for Approve and Reject
	/================================================*/
	const [selectedData, setSelectedData] = useState(defaultSelectedData);
	const [selectedAction, setSelectedAction] = useState(defaultSelectedAction);
	const [selectedAllData, setSelectedAllData] = useState(null);

	// Service Provider Modal - Select Service Providers
	const { visible: initiateServiceProviderModal, show: showServiceProviderModal, hide: hideServiceProviderModal } = useModal();

	// Multi Select Filter Dropdown Items
	const [selectServiceProviderItems, setSelectServiceProviderItems] = useState([]);
	const [selectEmailsForServiceProviders, setSelectEmailsForServiceProviders] = useState([]);
	const [selectPitstops, setSelectPitstops] = useState([]);

	// Memoized pitstop options to avoid recreating array on every render
	const pitstopOptions = useMemo(() => {
		return pitstops?.map((pitstop) => ({
			value: pitstop.systemId,
			label: pitstop.systemName,
			id: pitstop.systemId,
			orgId: pitstop.orgId
		})) || [];
	}, [pitstops]);

	// Feature Flag
	const isCopyTransactionEnabled = useFeature(featureConstant.ENABLE_COPY_TRANSACTION);

	useEffect(() => {
		reset();
		fetchDataExchange();
		fetchServiceProviderRelation();
	}, [refreshData]);

	useEffect(() => {
		fetchDataElements();
		fetchPitstops();
		fetchOrgData();
		fetchServiceProviderRelation();
	}, []);

	useEffect(() => {
		if (selectedAction) {
			if (selectedAction === "APPROVED") {
				showServiceProviderModal();
			} else if (selectedAction === "LAST_RECORD") {
				showLastRecordModal();
			} else {
				if (pitstops?.length === 1) {
					setSelectPitstops([{
						id: pitstops[0].systemId,
						label: pitstops[0].systemName,
						value: pitstops[0].systemId,
						orgId: pitstops[0].orgId
					}]);
				} else {
					setSelectPitstops([]);
				}
				showActionButtonModal();
			}
		}
	}, [selectedAction, pitstops])

	useEffect(() => {
		if (selectServiceProviderItems?.find(item => item.value === 'providingData') && pitstops?.length === 1) {
			setSelectPitstops([{
				id: pitstops[0].systemId,
				label: pitstops[0].systemName,
				value: pitstops[0].systemId
			}]);
		}
		else {
			setSelectPitstops([]);
		}
	}, [selectServiceProviderItems]);

	// Dropdown Search Handler
	const [dropdownSearch, setDropdownSearch] = useState({
		serviceProviderSearch: '',
	})

	const dropdownSearchHandler = (e) => {
		if (e?.target) {
			const { name, value } = e.target;
			setDropdownSearch(prev => ({ ...prev, [name]: value }));
		}
	}

	function reset() {
		hideActionButtonModal();
		hideServiceProviderModal();
		hideLastRecordModal();
		setSelectServiceProviderItems([]);
		setSelectEmailsForServiceProviders([]);
		setSelectPitstops([]);
	}

	function handleAction(data, type) {
		if (type === "CANCELLED" && isLastCombination(dataExchangeListing, data)) {
			setSelectedAction("LAST_RECORD");
			setSelectedData(data);
		} else {
			setSelectedAction(type);
			setSelectedData(data);
		}
	}

	function handleActionAll(data, type) {
		setSelectedData(null);
		setSelectedAction(type);
		setSelectedAllData(data);
	}

	async function handleAllSubmit() {
		await Promise.all((selectedAllData || []).map(data => handleSubmit(data)));
		return setRefreshData(!refreshData);
	}

	async function handleSPRelationshipSubmit() {
		return selectedData ? handleSubmit(selectedData) : handleAllSubmit();
	}

	async function handleSubmit(selectedData) {
		setLoading(true);

		let payload;
		const serviceProvidersLists = [];

		const createBody = (orgId) => {
			return {
				"serviceProviderOrgId": orgId,
			};
		};

		const createEmailBody = (email) => {
			return {
				"invitedViaEmail": email,
			};
		};

		const addServiceProvider = (orgItems) => {
			if (orgItems === 'providingData') {
				serviceProvidersLists.push(createBody(orgId));
			}
			else if (orgItems === 'inviteNewUser') {
				selectEmailsForServiceProviders.forEach(email => {
					serviceProvidersLists.push(createEmailBody(email));
				});
			}
			else {
				serviceProvidersLists.push(createBody(orgItems));
			}
		};

		if (selectServiceProviderItems?.length > 0) {
			selectServiceProviderItems?.map(item => addServiceProvider(item.value))
		}

		if (selectedAction === "APPROVED") {
			payload = {
				dataExchangeRelationshipId: selectedData.dataExchangeRelationshipId,
				serviceProviders: serviceProvidersLists,
				pitstops: selectPitstops?.map((value) => value.id) ?? []
			};
			const [error, response] = await api.dataExchangeRelationsApprove(payload);
			if (error) {
				setLoading(false);
				return addToastMessage(error?.message, toastType.error)
			}
			addToastMessage(response?.message ?? "Data exchange relationship approved", toastType.success);
		}
		else {
			payload = {};
			const {
				dataExchangeRelationshipId,
				dataExchangeRelationshipInvitesId,
				serviceProviderRelationshipId,
				serviceProviderRelationshipInvitesId
			} = selectedData;

			payload = {
				...payload,
				...(dataExchangeRelationshipId && { dataExchangeRelationshipId }),
				...(dataExchangeRelationshipInvitesId && { dataExchangeRelationshipInvitesId }),
				...(serviceProviderRelationshipId && { serviceProviderRelationshipId }),
				...(serviceProviderRelationshipInvitesId && { serviceProviderRelationshipInvitesId })
			};

			const { apiCall, successMessage } = renderAPIAction(selectedAction);
			const [error, response] = await apiCall(payload);
			if (error) {
				setLoading(false);
				return addToastMessage(error?.message, toastType.error);
			}
			addToastMessage(response?.message ?? successMessage, toastType.success);
		}

		setLoading(false);
		setSelectedAction(null);
		reset();
		return setRefreshData(!refreshData);
	}

	const handleConfirmLastRecordCancel = async (selectedRowData) => {
		setLoading(true)
		const payload = {
			dataExchangeRelationshipId: selectedRowData?.dataExchangeRelationshipId,
			serviceProviderOrgId: orgId,
			serviceProviderRelationshipId: selectedRowData?.serviceProviderRelationshipId,
		}
		payload.pitstops = pitstops.length > 1 ? (isCopyTransactionEnabled ? [selectPitstops[0].id] : selectPitstops) : [pitstops[0].systemId]
		const [error, response] = await api.editDataExchangeRelationship(payload)
		if (error) {
			setLoading(false);
			return addToastMessage(error?.message, toastType.error)
		}

		addToastMessage(response?.message ?? "Data exchange relationship updated", toastType.success);
		setLoading(false);
		setSelectedAction(null);
		reset();
		return setRefreshData(!refreshData);
	}

	const handleCopyTransaction = async (enableCopy) => {
		if (!selectedData?.dataExchangeRelationshipId && !selectedData?.dataExchangeRelationshipInvitesId) {
			return;
		}
		setLoading(true);
		const payload = getPayloadForDataExchangeRelationship(selectedData, enableCopy, selectPitstops);
		const [error, response] = await api.editDataExchangeRelationship(payload);
		setLoading(false);
		if (error) {
			return addToastMessage(error?.message, toastType.error);
		}
		addToastMessage(response?.message ?? `Copy transaction ${enableCopy ? "enabled" : "disabled"} successfully`, toastType.success);
		reset();
		setSelectedAction(null);
		setRefreshData(!refreshData);
	};

	// HANDLER FUNCTIONS
	const renderButton = (action) => {
		switch (action) {
			case "APPROVED":
				return <Button
					text="Approve"
					variant={ButtonVariant.STANDARD}
					type={ButtonType.SOLID}
					data-testid="approve"
					onClick={() => {
						hideActionButtonModal();
						showServiceProviderModal();
					}}
					id="my-data-exchange-modal-approve-button"
				/>
			case "REJECTED":
				return <Button
					text="Reject"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					data-testid="reject"
					onClick={() => {
						selectedData ? handleSubmit(selectedData) : handleAllSubmit();
					}}
					id="my-data-exchange-modal-reject-button"
				/>
			case "REVOKED":
				return <Button
					text="Revoke"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					data-testid="revoke"
					onClick={() => {
						selectedData ? handleSubmit(selectedData) : handleAllSubmit();
					}}
					id="my-data-exchange-modal-revoke-button"
				/>
			case "REMOVE":
				return <Button
					text="Remove"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					data-testid="remove"
					onClick={() => {
						selectedData ? handleSubmit(selectedData) : handleAllSubmit();
					}}
					id="my-data-exchange-modal-remove-button"
				/>
			case "CANCELLED":
				return <Button
					text="Confirm"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					data-testid="cancel-request"
					onClick={() => {
						selectedData ? handleSubmit(selectedData) : handleAllSubmit();
					}}
					id="my-data-exchange-modal-cancel-req-confirm-button"
				/>
			case "LAST_RECORD":
				return <Button
					text="Confirm"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					disabled={pitstops.length > 1 && selectPitstops.length === 0}
					data-testid="confirm-last-record"
					onClick={() => {
						handleConfirmLastRecordCancel(selectedData)
					}}
					id="my-data-exchange-modal-last-record-confirm-button"
				/>
			case "DISABLED_COPY_TRANSACTION":
				return <Button
					text="Disable"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					data-testid="disable-transaction-copy"
					onClick={() => {
						handleCopyTransaction(false);
					}}
				/>
			case "ENABLED_COPY_TRANSACTION":
				return <Button
					text="Enable"
					variant={ButtonVariant.STANDARD}
					type={ButtonType.SOLID}
					data-testid="enable-transaction-copy"
					onClick={() => {
						handleCopyTransaction(true);
					}}
					disabled={selectPitstops?.length === 0}
				/>
		}
	}

	// Custom Group Header Render Approve and Reject All
	const customGroupHeaderRender = (entries) => {
		const pendingEntries = entries.filter((entry) => {
			const shouldShowApproveAllAndRejectAll =
				entry.dataExchangeRelationshipStatus === "PENDING" &&
				(
					(entry.initiater === "SENDER" && entry.senderOrgId !== orgId) ||
					(entry.initiater === "RECEIVER" && entry.receiverOrgId !== orgId)
				);
			return shouldShowApproveAllAndRejectAll;
		});
		return (pendingEntries.length > 0) ? (<div className={`ds-multi-layer-table__group-header__right`}>
			<div className="ds-multi-layer-table__group__header__right__content">
				<div className="ds-multi-layer-table__group__header__right__content__reject" role="none" onClick={() => handleActionAll(pendingEntries, "REJECTED")} id="my-data-exchange-reject-all-button">Reject All</div>
				<div className="ds-multi-layer-table__group__header__right__content__approve" role="none" onClick={() => handleActionAll(pendingEntries, "APPROVED")} id="my-data-exchange-approve-all-button">Approve All</div>
			</div>
		</div>) : null;
	};

	return (<>
		<DataExchangeTablePage
			title={title}
			description={description}
			groupBy={groupBy}
			data={dataExchangeListing}
			emptyMessage={emptyMessage}
			refreshData={refreshData}
			setRefreshData={setRefreshData}
			customGroupHeaderRender={customGroupHeaderRender}
			organizationItems={allOrgData}
			serviceProvidersItems={allOrgData}
			serviceProviderRelationsLists={serviceProviderRelation}
			pitstopItems={pitstops}
			dataElements={dataElement}
			handleAction={handleAction}
			dataExchangeLoading={dataExchangeLoading}
			orgDataLoading={orgDataLoading}
			serviceProviderRelationLoading={serviceProviderRelationLoading}
			dataElementLoading={dataElementLoading}
			pitstopsLoading={pitstopsLoading}
		/>

		<Modal visible={initiateLastRecordModal} variant="medium" dismissClickOutsideModal={false}>
			<CardConfigure>
				<CardConfigure.Header>
					<CardConfigure.Header.Title>
						{RenderModalTitle(selectedAction)}
					</CardConfigure.Header.Title>
				</CardConfigure.Header>
				<CardConfigure.Body>
					<CardConfigure.Body.Col>
						{renderMessage(selectedAction)}
						{pitstops.length > 1 && (
							<div className="card-configure__body__dropdown__option">
								<span className="card-configure__body__dropdown__title">
									Pitstop
								</span>
								<InputDropdown
									placeholder={"Select your Pitstop(s) to host"}
									dropdownOption={pitstops.map((pitstop) => { return { value: pitstop.systemId, label: pitstop.systemName, id: pitstop.systemId } })}
									dropdownSelectedOption={selectPitstops}
									onSelectDropdownItem={setSelectPitstops}
									multipleSelection={false}
									value={dropdownSearch.pitstopSearch}
									onChange={dropdownSearchHandler}
									name={'pitstopSearch'}
									fullWidth={isCopyTransactionEnabled}
								/>
							</div>
						)}
					</CardConfigure.Body.Col>
				</CardConfigure.Body>
				<CardConfigure.Footer>
					<CardConfigure.Footer.Left>
						<Button
							text="Cancel"
							variant={selectedAction === "APPROVED"? ButtonVariant.STANDARD : ButtonVariant.DESTRUCTIVE}
							type={ButtonType.GHOST}
							data-testid="last-record-cancel-button"
							addClass={selectedAction === "APPROVED" ? "" : "on-boarding__card-configure__footer-warning"}
							onClick={() => {
								setSelectedAction(null);
								hideActionButtonModal();
								hideLastRecordModal();
								hideServiceProviderModal();
								setSelectPitstops([]);
							}}
							id="my-data-exchange-modal-cancel-button"
						/>
					</CardConfigure.Footer.Left>
					<CardConfigure.Footer.Right>
						{renderButton(selectedAction)}
					</CardConfigure.Footer.Right>
				</CardConfigure.Footer>
			</CardConfigure>
		</Modal>

		<Modal visible={initiateActionButtonModal} close={() => { setSelectedAction(null); hideActionButtonModal(); }} variant="medium" dismissClickOutsideModal={false}>
			<CardConfigure>
				<CardConfigure.Header>
					<CardConfigure.Header.Title>
						{RenderModalTitle(selectedAction)}
					</CardConfigure.Header.Title>
				</CardConfigure.Header>
				<CardConfigure.Body>
					<CardConfigure.Body.Col>
						{selectedAction === "ENABLED_COPY_TRANSACTION" && <div className="card-configure__body__dropdown">
							<div className="card-configure__body__dropdown__option">
								<span className="card-configure__body__dropdown__title">
									Pitstop
								</span>
								{pitstops?.length > 1 && <div className="card-configure__body__dropdown__option">
									<InputDropdown
										placeholder={"Select your Pitstop to sent these copies"}
										dropdownOption={pitstopOptions}
										dropdownSelectedOption={selectPitstops}
										onSelectDropdownItem={setSelectPitstops}
										multipleSelection={false}
										dataTestId="copy-transaction-pitstop-select"
										fullWidth
									/>
								</div>}
								{pitstops?.length === 1 && <div className="copy-transaction__single-input__body">
									<div className='copy-transactions__single-input-container'>{selectPitstops[0]?.label}</div>
								</div>}
							</div>
						</div>}
						{renderMessage(selectedAction)}
					</CardConfigure.Body.Col>
				</CardConfigure.Body>
				<CardConfigure.Footer>
					<CardConfigure.Footer.Left>
						<Button
							text="Cancel"
							variant={(selectedAction === "APPROVED" || selectedAction === "ENABLED_COPY_TRANSACTION") ? ButtonVariant.STANDARD : ButtonVariant.DESTRUCTIVE}
							type={ButtonType.GHOST}
							data-testid="cancel"
							addClass={selectedAction === "APPROVED" ? "" : "on-boarding__card-configure__footer-warning"}
							onClick={() => {
								setSelectedAction(null);
								hideActionButtonModal();
							}}
							id="my-data-exchange-modal-cancel-button"
						/>
					</CardConfigure.Footer.Left>
					<CardConfigure.Footer.Right>
						{renderButton(selectedAction)}
					</CardConfigure.Footer.Right>
				</CardConfigure.Footer>
			</CardConfigure>
		</Modal>

		<Modal visible={initiateServiceProviderModal} close={() => { setSelectedAction(null); hideServiceProviderModal(); }} variant="small" dismissClickOutsideModal={false}>
			<CardConfigure >
				<CardConfigure.Header>
					<CardConfigure.Header.Title>
						<div className="card-configure__header__left">
							<div className="card-configure__title">
								<div className="card-configure__title__left--background">
									<div className='DS__icon'>
										<IconDataExchange />
									</div>
								</div>
								<div className="card-configure__title__right">
									<div className="card-configure__title__right__top">
										<div className='card-configure__title__right__top__title'>
											Data Exchange Consent
										</div>
									</div>
								</div>
							</div>
						</div>
					</CardConfigure.Header.Title>
				</CardConfigure.Header>
				<CardConfigure.Body>
					<CardConfigure.Body.Col>
						<div className='on-boarding__card-configure__body__heading'>
							You are about to <b>approve</b> this data exchange relationship.
						</div>
						<div className='on-boarding__card-configure__body__heading'>
							Would you like to set up your Solution Provider for this data exchange relationship?
						</div>
						<div className="card-configure__body__dropdown__option">
							<span className="card-configure__body__dropdown__title">
								Via
							</span>
							<InputDropdown
								placeholder={"Select Organization(s)"}
								name={"serviceProviderSearch"}
								dropdownOption={[{ id: 'inviteNewUser', label: 'Invite New User', value: 'inviteNewUser' }, { id: 'providingData', label: 'I will provide/consume the data', value: 'providingData' }, ...allOrgData.filter((org) => org.id != orgId).map((org) => { return { value: org.id, label: org.name, id: org.id } })]}
								onSelectDropdownItem={setSelectServiceProviderItems}
								multipleSelection
								dropdownSelectedOption={selectServiceProviderItems}
								value={dropdownSearch.serviceProviderSearch}
								onChange={dropdownSearchHandler}
								isLoading={orgDataLoading}
							/>
						</div>
						{selectServiceProviderItems?.find(item => item.value === 'inviteNewUser') &&
							<MultipleEmailAddressInput
								emailList={selectEmailsForServiceProviders}
								setEmailList={setSelectEmailsForServiceProviders}
								emailText={"Can't find the Solution Provider you are looking for?"}
								validateExistingEmail={true}
							/>
						}
						{(selectServiceProviderItems?.find(item => item.value === 'providingData') && pitstops.length > 1) && <div className="card-configure__body__dropdown">
							<div className="card-configure__body__dropdown__option">
								<span className="card-configure__body__dropdown__title">
									Pitstop
								</span>
								<InputDropdown
									placeholder={"Select your Pitstop(s) to host"}
									dropdownOption={pitstops.map((pitstop) => { return { value: pitstop.systemId, label: pitstop.systemName, id: pitstop.systemId } })}
									onSelectDropdownItem={setSelectPitstops}
									dropdownSelectedOption={selectPitstops}
									value={dropdownSearch.pitstopSearch}
									onChange={dropdownSearchHandler}
									multipleSelection
									name={'pitstopSearch'}
								/>
							</div>
						</div>}
					</CardConfigure.Body.Col>
				</CardConfigure.Body>
				<CardConfigure.Footer>
					<CardConfigure.Footer.Left>
						<Button
							text="Cancel"
							data-testid="cancel-selection-service-provider"
							variant={ButtonVariant.STANDARD}
							type={ButtonType.GHOST}
							onClick={() => {
								setSelectedAction(null);
								hideServiceProviderModal()
							}}
							id="my-data-exchange-modal-cancel-button"
						/>
					</CardConfigure.Footer.Left>
					<CardConfigure.Footer.Right>
						<Button
							text="Approve"
							data-testid="add-service-provider"
							variant={ButtonVariant.STANDARD}
							type={ButtonType.SOLID}
							disabled={selectServiceProviderItems?.length === 0 ||
								(selectServiceProviderItems?.find(item => item.value === 'inviteNewUser') && selectEmailsForServiceProviders.length === 0) ||
								(selectServiceProviderItems?.find(item => item.value === 'providingData') && selectPitstops.length === 0)}
							onClick={() => {
								handleSPRelationshipSubmit()
							}}
							id="my-data-exchange-modal-approve-button"
						/>
					</CardConfigure.Footer.Right>
				</CardConfigure.Footer>
			</CardConfigure>
		</Modal>

		<Toast toastList={toastList} />

		<Portal id="app-blocker">
			{loading && (
				<div className="modal white">
					<div className="loading">
						<img src={assetUrl("/loading.gif")} alt="Loading" />
					</div>
				</div>
			)}
		</Portal>
	</>);
};

export default DataExchangePage;

DataExchangePage.propTypes = {
	defaultSelectedAction: PropTypes.string,
	defaultSelectedData: PropTypes.object
};
